import { inject } from '@angular/core';
import {
  ChatDaemon,
  ChatSystemNotificationBackgroundRegistration,
} from 'ias-ppr-mt-chat-client';
import { NativePushService } from '@core/services/native-shell/native-push.service';
import { NativeShellService } from '@core/services/native-shell/native-shell.service';
import { APPLICATION_SESSION } from '@core/services/auth/application-session';

/** The shared controller owns the preference; Android owns the background installation. */
export function pwaChatBackgroundRegistration(
  browser: ChatSystemNotificationBackgroundRegistration,
): ChatSystemNotificationBackgroundRegistration {
  const shell = inject(NativeShellService);
  const push = inject(NativePushService);
  const session = inject(APPLICATION_SESSION);
  const chat = inject(ChatDaemon);
  let binding: { userId: string; subject: string; generation: number } | null =
    null;
  const current = (userId: string, subject: string, generation: number) =>
    session.authenticated &&
    session.subject === subject &&
    shell.generation() === generation &&
    chat.currentUser()?.id === userId;
  const release = async (userId: string) => {
    const previous = binding;
    if (
      !previous ||
      previous.userId !== userId ||
      previous.generation !== shell.generation() ||
      previous.subject !== session.subject
    )
      return;
    // Disposing a web chat context is not a native IAM/session boundary.
    // Explicit deactivation owns the preference; Android owns session termination.
    if (binding === previous) binding = null;
  };
  return {
    activate: async (userId, capability) => {
      if (!shell.present) return browser.activate(userId, capability);
      const subject = session.subject;
      const generation = shell.generation();
      if (generation === null || !current(userId, subject, generation))
        return false;
      await shell.initialize();
      if (!push.available() || !current(userId, subject, generation))
        return false;
      const state = await push.setEnabled(true, generation);
      if (!current(userId, subject, generation)) return false;
      binding = { userId, subject, generation };
      return state.enabled;
    },
    deactivate: async (userId) => {
      if (!shell.present) return browser.deactivate(userId);
      const subject = session.subject;
      const generation = shell.generation();
      if (generation === null || !current(userId, subject, generation)) return;
      await shell.initialize();
      if (!push.available() || !current(userId, subject, generation)) return;
      await push.setEnabled(false, generation);
      if (binding?.userId === userId && binding.generation === generation)
        binding = null;
    },
    release: (userId) =>
      shell.present ? release(userId) : browser.release(userId),
  };
}
